import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { LoginRequest, LoginResponse, UserInfo, AuthState, RefreshTokenRequest, RefreshTokenResponse } from '../models/auth.models';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'travvise_tenant_token';
  private readonly REFRESH_TOKEN_KEY = 'travvise_tenant_refresh_token';
  private readonly USER_KEY = 'travvise_tenant_user';
  private readonly EXPIRES_AT_KEY = 'travvise_tenant_expires_at';
  private readonly LAST_ACTIVITY_KEY = 'travvise_tenant_last_activity';

  private authStateSubject = new BehaviorSubject<AuthState>(this.getInitialAuthState());
  public authState$ = this.authStateSubject.asObservable();

  private tokenRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private readonly HEARTBEAT_INTERVAL = 60000; // Check every 60 seconds
  private readonly ACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes of inactivity
  private isRefreshing = false;

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    // Check token expiration on service initialization
    this.checkTokenExpiration();

    // Listen for page visibility changes to check token when user returns
    this.setupVisibilityListener();

    // Setup activity tracking
    this.setupActivityTracking();

    // Start periodic token validation heartbeat
    this.startHeartbeat();
  }

  /**
   * Get initial auth state from localStorage
   */
  private getInitialAuthState(): AuthState {
    const token = localStorage.getItem(this.TOKEN_KEY);
    const userJson = localStorage.getItem(this.USER_KEY);
    const expiresAt = localStorage.getItem(this.EXPIRES_AT_KEY);

    if (token && userJson && expiresAt) {
      const expiresAtNum = parseInt(expiresAt, 10);
      const now = Date.now();

      // Check if token is still valid
      if (now < expiresAtNum) {
        return {
          IsAuthenticated: true,
          User: JSON.parse(userJson),
          Token: token,
          ExpiresAt: expiresAtNum
        };
      } else {
        // Token expired, clear storage
        this.clearAuthData();
      }
    }

    return {
      IsAuthenticated: false,
      User: null,
      Token: null,
      ExpiresAt: null
    };
  }

  /**
   * Login user
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http.post<Record<string, unknown>>(`${environment.authApiUrl}/api/auth/login`, credentials).pipe(
      map(apiResponse => {
        // Transform API response to match LoginResponse interface
        const response: LoginResponse = {
          Token: (apiResponse['AccessToken'] || apiResponse['Token'] || apiResponse['token']) as string,
          RefreshToken: (apiResponse['RefreshToken'] || apiResponse['refreshToken']) as string,
          ExpiresIn: (apiResponse['ExpiresIn'] || apiResponse['expiresIn'] || 3600) as number,
          User: {
            UserId: (apiResponse['UserId'] || apiResponse['userId']) as string,
            Username: (apiResponse['Email'] || apiResponse['email'] || apiResponse['username']) as string,
            Email: (apiResponse['Email'] || apiResponse['email']) as string,
            FirstName: (apiResponse['FirstName'] || apiResponse['firstName']) as string,
            LastName: (apiResponse['LastName'] || apiResponse['lastName']) as string,
            Role: (apiResponse['Role'] || apiResponse['role']) as string,
            Permissions: (apiResponse['Permissions'] || apiResponse['permissions'] || []) as string[],
            TenantId: (apiResponse['TenantId'] || apiResponse['tenantId']) as string | undefined,
            TenantName: (apiResponse['TenantName'] || apiResponse['tenantName']) as string | undefined
          }
        };
        return response;
      }),
      tap(response => {
        this.handleLoginSuccess(response);
      }),
      catchError(error => {
        console.error('Login failed:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Handle successful login
   */
  private handleLoginSuccess(response: LoginResponse): void {
    const expiresAt = Date.now() + (response.ExpiresIn * 1000);

    // Store auth data
    localStorage.setItem(this.TOKEN_KEY, response.Token);
    localStorage.setItem(this.USER_KEY, JSON.stringify(response.User));
    localStorage.setItem(this.EXPIRES_AT_KEY, expiresAt.toString());
    localStorage.setItem(this.LAST_ACTIVITY_KEY, Date.now().toString());

    if (response.RefreshToken) {
      localStorage.setItem(this.REFRESH_TOKEN_KEY, response.RefreshToken);
    }

    // Update auth state
    this.authStateSubject.next({
      IsAuthenticated: true,
      User: response.User,
      Token: response.Token,
      ExpiresAt: expiresAt
    });

    // Schedule token refresh
    this.scheduleTokenRefresh(response.ExpiresIn);

    // Start heartbeat monitoring
    this.startHeartbeat();
  }

  /**
   * Logout user
   */
  logout(): void {
    // Call logout API if needed
    this.http.post(`${environment.authApiUrl}/api/auth/logout`, {}).subscribe({
      next: () => console.log('Logged out from server'),
      error: (err) => console.error('Logout error:', err)
    });

    // Clear local data
    this.clearAuthData();

    // Navigate to login
    this.router.navigate(['/login']);
  }

  /**
   * Get current auth token
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Decode JWT and return the TenantId claim directly from the token payload.
   * More reliable than getCurrentUser() which depends on what was stored at login time.
   */
  getTenantIdFromToken(): string | null {
    const token = this.getToken();
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const tenantId = payload['TenantId'] || payload['tenantId'] || payload['tenant_id'] || null;
      const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
      return tenantId && tenantId !== EMPTY_GUID ? tenantId : null;
    } catch {
      return null;
    }
  }

  /**
   * Get current user
   */
  getCurrentUser(): UserInfo | null {
    const userJson = localStorage.getItem(this.USER_KEY);
    return userJson ? JSON.parse(userJson) : null;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    const expiresAt = localStorage.getItem(this.EXPIRES_AT_KEY);

    if (!token || !expiresAt) {
      return false;
    }

    const expiresAtNum = parseInt(expiresAt, 10);
    return Date.now() < expiresAtNum;
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user?.Role === role;
  }

  /**
   * Check if user is SuperAdmin
   */
  isSuperAdmin(): boolean {
    return this.hasRole('Super Admin') || this.hasRole('SuperAdmin');
  }

  /**
   * Check if user is Platform Admin (Travvise team member)
   * Platform admins can manage all tenants
   */
  isPlatformAdmin(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    try {
      // Decode JWT token (simple base64 decode of payload)
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.is_platform_admin === 'true' || payload.is_platform_admin === true;
    } catch (error) {
      console.error('Error decoding token:', error);
      return false;
    }
  }

  /**
   * Check if user has specific permission
   */
  hasPermission(permission: string): boolean {
    const user = this.getCurrentUser();
    return user?.Permissions?.includes(permission) || false;
  }

  /**
   * Refresh authentication token
   */
  refreshToken(): Observable<RefreshTokenResponse> {
    const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);

    if (!refreshToken) {
      console.warn('No refresh token available');
      return throwError(() => new Error('No refresh token available'));
    }

    // Prevent multiple simultaneous refresh attempts
    if (this.isRefreshing) {
      console.log('Token refresh already in progress, skipping...');
      return throwError(() => new Error('Token refresh already in progress'));
    }

    this.isRefreshing = true;
    const request: RefreshTokenRequest = { RefreshToken: refreshToken };

    console.log('Attempting to refresh token...');
    return this.http.post<RefreshTokenResponse>(`${environment.authApiUrl}/api/auth/refresh`, request).pipe(
      tap(response => {
        this.isRefreshing = false;
        console.log('Token refresh successful');
        const expiresAt = Date.now() + (response.ExpiresIn * 1000);

        localStorage.setItem(this.TOKEN_KEY, response.Token);
        localStorage.setItem(this.REFRESH_TOKEN_KEY, response.RefreshToken);
        localStorage.setItem(this.EXPIRES_AT_KEY, expiresAt.toString());

        const currentState = this.authStateSubject.value;
        this.authStateSubject.next({
          ...currentState,
          Token: response.Token,
          ExpiresAt: expiresAt
        });

        this.scheduleTokenRefresh(response.ExpiresIn);
      }),
      catchError(error => {
        this.isRefreshing = false;
        console.error('Token refresh failed:', error);
        console.error('Error status:', error.status);
        console.error('Error details:', error.error);

        // Only logout if refresh token is invalid (401/403)
        // Don't logout on network errors (0, 504, etc.)
        if (error.status === 401 || error.status === 403) {
          console.log('Refresh token invalid, logging out...');
          this.logout();
        } else {
          console.log('Temporary error, will retry on next visibility change');
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Schedule automatic token refresh
   */
  private scheduleTokenRefresh(expiresIn: number): void {
    // Clear any existing timer
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }

    // Refresh token 5 minutes before expiration
    const refreshTime = (expiresIn - 300) * 1000;

    if (refreshTime > 0) {
      console.log(`Scheduling token refresh in ${Math.round(refreshTime / 1000)} seconds`);
      this.tokenRefreshTimer = setTimeout(() => {
        if (this.isAuthenticated() && !this.isRefreshing) {
          console.log('Scheduled refresh triggered');
          this.refreshToken().subscribe({
            error: (err) => {
              console.error('Scheduled refresh failed:', err);
              // Retry after 30 seconds if failed
              this.tokenRefreshTimer = setTimeout(() => {
                this.refreshToken().subscribe();
              }, 30000);
            }
          });
        }
      }, refreshTime);
    }
  }

  /**
   * Check token expiration on startup
   */
  private checkTokenExpiration(): void {
    const expiresAt = localStorage.getItem(this.EXPIRES_AT_KEY);

    if (expiresAt) {
      const expiresAtNum = parseInt(expiresAt, 10);
      const now = Date.now();
      const timeUntilExpiry = expiresAtNum - now;

      if (timeUntilExpiry <= 0) {
        // Token expired, try to refresh
        console.log('Token expired, attempting refresh...');
        this.refreshToken().subscribe({
          error: () => this.logout()
        });
      } else if (timeUntilExpiry < 300000) {
        // Less than 5 minutes until expiry, refresh now
        console.log('Token expiring soon, refreshing...');
        this.refreshToken().subscribe();
      } else {
        // Schedule refresh
        this.scheduleTokenRefresh(timeUntilExpiry / 1000);
      }
    }
  }

  /**
   * Setup visibility change listener
   * When user returns to tab, check if token needs refresh
   */
  private setupVisibilityListener(): void {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          console.log('Tab became visible, checking token status...');

          // Restart heartbeat when tab becomes visible
          this.startHeartbeat();

          const expiresAt = localStorage.getItem(this.EXPIRES_AT_KEY);
          if (expiresAt) {
            const expiresAtNum = parseInt(expiresAt, 10);
            const now = Date.now();
            const timeUntilExpiry = expiresAtNum - now;

            if (timeUntilExpiry <= 0) {
              // Token expired while tab was hidden
              console.log('Token expired while tab was hidden, attempting refresh...');
              this.refreshToken().subscribe({
                error: () => {
                  console.log('Refresh failed, logging out...');
                  this.logout();
                }
              });
            } else if (timeUntilExpiry < 600000) {
              // Less than 10 minutes until expiry, refresh proactively
              console.log('Token will expire soon, refreshing proactively...');
              this.refreshToken().subscribe({
                error: (err) => console.error('Proactive refresh failed:', err)
              });
            }
          }
        } else {
          // Stop heartbeat when tab is hidden to conserve resources
          this.stopHeartbeat();
        }
      });
    }
  }

  /**
   * Start periodic token validation heartbeat
   */
  private startHeartbeat(): void {
    // Clear any existing heartbeat
    this.stopHeartbeat();

    if (!this.isAuthenticated()) {
      return;
    }

    console.log('Starting authentication heartbeat');
    this.heartbeatTimer = setInterval(() => {
      if (!this.isAuthenticated()) {
        this.stopHeartbeat();
        return;
      }

      const expiresAt = localStorage.getItem(this.EXPIRES_AT_KEY);
      if (expiresAt) {
        const expiresAtNum = parseInt(expiresAt, 10);
        const now = Date.now();
        const timeUntilExpiry = expiresAtNum - now;

        // If token expires in less than 10 minutes, refresh it
        if (timeUntilExpiry > 0 && timeUntilExpiry < 600000 && !this.isRefreshing) {
          console.log(`Heartbeat: Token expires in ${Math.round(timeUntilExpiry / 1000)}s, refreshing...`);
          this.refreshToken().subscribe({
            error: (err) => console.error('Heartbeat refresh failed:', err)
          });
        }

        // Check for inactivity timeout
        this.checkInactivityTimeout();
      }
    }, this.HEARTBEAT_INTERVAL);
  }

  /**
   * Stop periodic token validation heartbeat
   */
  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      console.log('Stopping authentication heartbeat');
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * Setup activity tracking to prevent timeout during active use
   */
  private setupActivityTracking(): void {
    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
      const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
      const updateActivity = () => {
        if (this.isAuthenticated()) {
          localStorage.setItem(this.LAST_ACTIVITY_KEY, Date.now().toString());
        }
      };

      activityEvents.forEach(event => {
        document.addEventListener(event, updateActivity, true);
      });

      // Set initial activity timestamp
      updateActivity();
    }
  }

  /**
   * Check if user has been inactive for too long
   */
  private checkInactivityTimeout(): void {
    const lastActivityStr = localStorage.getItem(this.LAST_ACTIVITY_KEY);
    if (!lastActivityStr) {
      return;
    }

    const lastActivity = parseInt(lastActivityStr, 10);
    const now = Date.now();
    const inactiveDuration = now - lastActivity;

    if (inactiveDuration > this.ACTIVITY_TIMEOUT) {
      console.log('User inactive for too long, logging out...');
      this.logout();
    }
  }

  /**
   * Exchange a short-lived SSO nonce for the actual token + user blob.
   * The backend deletes the nonce on first use (single-use, 5-min TTL).
   */
  exchangeSsoNonce(nonce: string): Observable<{ sso_token: string; sso_user: string }> {
    return this.http.get<{ sso_token: string; sso_user: string }>(
      `${environment.authApiUrl}/api/azuread/exchange`,
      { params: { nonce } }
    );
  }

  /**
   * Store token received from SSO callback
   */
  storeTokenFromSSO(token: string, ssoUser: string): void {
    const user: UserInfo = JSON.parse(atob(ssoUser));
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiresAt = payload.exp * 1000;

    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    localStorage.setItem(this.EXPIRES_AT_KEY, expiresAt.toString());
    localStorage.setItem(this.LAST_ACTIVITY_KEY, Date.now().toString());

    this.authStateSubject.next({
      IsAuthenticated: true,
      User: user,
      Token: token,
      ExpiresAt: expiresAt
    });

    this.scheduleTokenRefresh((expiresAt - Date.now()) / 1000);
    this.startHeartbeat();
  }

  /**
   * Clear authentication data and timers
   */
  private clearAuthData(): void {
    // Clear timers
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
    this.stopHeartbeat();

    // Clear storage
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.EXPIRES_AT_KEY);
    localStorage.removeItem(this.LAST_ACTIVITY_KEY);

    // Update state
    this.authStateSubject.next({
      IsAuthenticated: false,
      User: null,
      Token: null,
      ExpiresAt: null
    });
  }
}
